import { trackClientError } from './tlfAnalytics.js';

const MAX_MESSAGE_LENGTH = 1000;
const MAX_STACK_LENGTH = 8000;
const MAX_COMPONENT_STACK_LENGTH = 8000;
const MAX_FILENAME_LENGTH = 1000;
const MAX_REPORTS_PER_SESSION = 20;
const MAX_CONCURRENT_REPORTS = 3;
const DEDUPE_WINDOW_MS = 60_000;

const TOKEN_PATTERN = /\b(?:eyJ[a-zA-Z0-9_-]{8,}\.[a-zA-Z0-9_-]{8,}(?:\.[a-zA-Z0-9_-]{8,})?|bearer\s+[a-zA-Z0-9._~-]+)\b/gi;
const BASIC_AUTH_PATTERN = /\bbasic\s+[a-zA-Z0-9+/=]+\b/gi;
const QUERY_PATTERN = /([?&][^\s#=]+)=([^\s&#]*)/g;
const SENSITIVE_ASSIGNMENT_PATTERN = /\b(password|passwd|pwd|token|reset[_-]?token|access[_-]?token|refresh[_-]?token|api[_-]?key|secret|authorization|auth|cvv|cvc|card(?:number)?|pan)\b(["']?)(\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^\s,;}]+)/gi;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PAYMENT_CARD_PATTERN = /\b(?:\d[ -]*?){13,19}\b/g;

let installedCleanup = null;

const truncate = (value, maxLength) => {
  if (typeof value !== 'string') return null;
  return value.length > maxLength ? value.slice(0, maxLength) : value;
};

export const sanitizeClientErrorText = (value, maxLength = MAX_MESSAGE_LENGTH) => {
  if (typeof value !== 'string') return null;
  return truncate(
    value
      .replace(TOKEN_PATTERN, '[redacted]')
      .replace(BASIC_AUTH_PATTERN, 'Basic [redacted]')
      .replace(QUERY_PATTERN, '$1=[redacted]')
      .replace(EMAIL_PATTERN, '[redacted-email]')
      .replace(PAYMENT_CARD_PATTERN, '[redacted-card]')
      .replace(SENSITIVE_ASSIGNMENT_PATTERN, '$1$2$3[redacted]'),
    maxLength
  );
};

export const sanitizeClientErrorUrl = (value) => {
  if (typeof value !== 'string' || value.length === 0) return null;

  try {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://client.invalid';
    const url = new URL(value, baseUrl);
    return truncate(`${url.origin}${url.pathname}`, MAX_FILENAME_LENGTH);
  } catch {
    return sanitizeClientErrorText(value.split(/[?#]/, 1)[0], MAX_FILENAME_LENGTH);
  }
};

const normalizeReason = (reason) => {
  if (reason instanceof Error) return reason;
  if (typeof reason === 'string') return new Error(reason);
  if (typeof reason === 'number' || typeof reason === 'boolean') return new Error(String(reason));
  if (reason && typeof reason === 'object') {
    const rawMessage = reason.message || reason.error || reason.statusText || (reason.status ? `Request failed with status ${reason.status}` : null);
    if (typeof rawMessage === 'string' && rawMessage.trim().length > 0) {
      const err = new Error(rawMessage);
      if (reason.name && typeof reason.name === 'string') err.name = reason.name;
      if (reason.stack && typeof reason.stack === 'string') err.stack = reason.stack;
      return err;
    }
  }
  return new Error('Unhandled promise rejection');
};

const getRelease = () => {
  if (typeof __TLF_BUILD_VERSION__ !== 'undefined') return __TLF_BUILD_VERSION__;

  try {
    return sessionStorage.getItem('buildVersion') || 'unknown';
  } catch {
    return 'unknown';
  }
};

export const normalizeClientError = ({
  source,
  error,
  message,
  filename,
  lineNumber,
  columnNumber,
  componentStack
}) => {
  const normalizedError = error instanceof Error ? error : normalizeReason(error ?? message);
  const normalizedMessage = message || normalizedError.message || 'Unknown client error';

  return {
    schemaVersion: 1,
    source,
    name: sanitizeClientErrorText(normalizedError.name || 'Error', 200),
    message: sanitizeClientErrorText(normalizedMessage),
    stack: sanitizeClientErrorText(normalizedError.stack, MAX_STACK_LENGTH),
    componentStack: sanitizeClientErrorText(componentStack, MAX_COMPONENT_STACK_LENGTH),
    filename: sanitizeClientErrorUrl(filename),
    lineNumber: Number.isFinite(lineNumber) ? lineNumber : null,
    columnNumber: Number.isFinite(columnNumber) ? columnNumber : null,
    release: sanitizeClientErrorText(getRelease(), 200)
  };
};

const createDedupeKey = (clientError) => [
  clientError.source,
  clientError.name,
  clientError.message,
  clientError.filename,
  clientError.lineNumber,
  clientError.columnNumber
].join('|');

export const createClientErrorReporter = ({
  send = trackClientError,
  now = () => Date.now(),
  maxReports = MAX_REPORTS_PER_SESSION,
  maxConcurrentReports = MAX_CONCURRENT_REPORTS,
  dedupeWindowMs = DEDUPE_WINDOW_MS
} = {}) => {
  const recentReports = new Map();
  const pendingReports = [];
  let reportCount = 0;
  let activeReports = 0;

  const dispatchPending = () => {
    while (activeReports < maxConcurrentReports && pendingReports.length > 0) {
      const clientError = pendingReports.shift();
      activeReports += 1;

      Promise.resolve()
        .then(() => send(clientError))
        .catch(() => {})
        .finally(() => {
          activeReports -= 1;
          dispatchPending();
        });
    }
  };

  return (details) => {
    if (reportCount >= maxReports) return false;

    const clientError = normalizeClientError(details);
    const dedupeKey = createDedupeKey(clientError);
    const timestamp = now();
    const previousTimestamp = recentReports.get(dedupeKey);

    if (previousTimestamp !== undefined && timestamp - previousTimestamp < dedupeWindowMs) {
      return false;
    }

    recentReports.set(dedupeKey, timestamp);
    reportCount += 1;
    pendingReports.push(clientError);
    dispatchPending();

    return true;
  };
};

const defaultReporter = createClientErrorReporter();

export const setupClientErrorTelemetry = () => {
  if (typeof window === 'undefined') return () => {};
  if (installedCleanup) return installedCleanup;

  const handleWindowError = (event) => {
    const target = event.target;
    if (target && target !== window) {
      if (target.tagName === 'SCRIPT') {
        defaultReporter({
          source: 'script-load',
          error: new Error('JavaScript resource failed to load'),
          filename: target.src
        });
      }
      // Ignore other non-script DOM element errors (e.g., broken images, stylesheets)
      return;
    }

    defaultReporter({
      source: 'window',
      error: event.error,
      message: event.message,
      filename: event.filename,
      lineNumber: event.lineno,
      columnNumber: event.colno
    });
  };

  const handleUnhandledRejection = (event) => {
    const error = normalizeReason(event.reason);
    defaultReporter({ source: 'unhandled-rejection', error });
  };

  window.addEventListener('error', handleWindowError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  installedCleanup = () => {
    window.removeEventListener('error', handleWindowError, true);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    installedCleanup = null;
  };

  return installedCleanup;
};

export const reportReactBoundaryError = (error, componentStack) => {
  return defaultReporter({ source: 'react-boundary', error, componentStack });
};
